import React, { useState, useMemo, useCallback } from 'react';
import { Page, Layout, Main, LoadingOverlay } from './styled';
import { Ring } from '@uiball/loaders';
import StoreBanner from './StoreBanner';
import ProductListSection from './ProductListSection';
import StoreInfoSidebar from './StoreInfoSidebar';

function filterItemsBySearch(items, searchTerm) {
  const tokens = (searchTerm || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return items;
  return items.filter((item) => {
    const searchable = [
      item._id,
      item.title,
      String(item.product_sizes_quantity?.[0]?.price ?? item.euro_price?.[0] ?? ''),
      item.symbol,
      item.lot_number,
      item.global_trade_item_number,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.every((t) => searchable.includes(t));
  });
}

export default function StoreDetailNewView({
  storeData,
  products = [],
  groups = [],
  lots = [],
  isLoading,
  onBack,
  onAddProduct,
  onAddLot,
  onCreateGroup,
  onImportProducts,
  onViewProductDetails,
  onCloneProduct,
  isStoreOwner,
}) {
  const [productSearch, setProductSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [filterType, setFilterType] = useState('products'); // 'products' | 'groups' | 'auction' | 'lots'

  const allItems = useMemo(() => [...groups, ...products, ...lots], [groups, products, lots]);

  const itemsByFilter = useMemo(() => {
    if (filterType === 'products') return allItems.filter((item) => !item.isGroup && !item.isLot);
    if (filterType === 'groups') return allItems.filter((item) => item.isGroup);
    if (filterType === 'auction') {
      return allItems.filter((item) => {
        const hasAuction = item.isGroup
          ? item.product?.[0]?.auction_detail?.[0]
          : item.auction_detail?.[0];
        return Boolean(hasAuction);
      });
    }
    if (filterType === 'lots') return allItems.filter((item) => item.isLot === true);
    return allItems;
  }, [allItems, filterType]);

  const filteredItems = useMemo(
    () => filterItemsBySearch(itemsByFilter, productSearch),
    [itemsByFilter, productSearch]
  );

  const lastBulkUpdate = useMemo(
    () =>
      new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    []
  );

  const handleViewProduct = useCallback(
    (item) => {
      onViewProductDetails?.(item);
    },
    [onViewProductDetails]
  );

  const handleCloneProduct = useCallback(
    (item) => {
      onCloneProduct?.(item);
    },
    [onCloneProduct]
  );

  if (isLoading) {
    return (
      <LoadingOverlay>
        <Ring size={65} color="#5ce9ff" />
      </LoadingOverlay>
    );
  }

  if (!storeData) {
    return null;
  }

  return (
    <Page>
      <Layout>
        <Main>
          <StoreBanner
            storeName={storeData.storeName}
            shortDescription={storeData.shortDescription || storeData.storeAddress}
            coverImage={storeData.storeLogo}
            onBack={onBack}
            onAddProduct={onAddProduct}
            onAddLot={onAddLot}
            onCreateGroup={onCreateGroup}
            onImportProducts={onImportProducts}
            isStoreOwner={isStoreOwner}
          />
          <ProductListSection
            totalCount={itemsByFilter.length}
            filteredCount={filteredItems.length}
            lastBulkUpdate={lastBulkUpdate}
            productSearch={productSearch}
            onSearchChange={setProductSearch}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            items={filteredItems}
            selectedId={selectedProductId}
            onSelectItem={(item) => setSelectedProductId(item._id)}
            onViewProduct={handleViewProduct}
            onCloneProduct={handleCloneProduct}
            onBuy={() => {}}
            symbol={storeData.symbol || products[0]?.symbol || 'USDT'}
            isStoreOwner={isStoreOwner}
            filterType={filterType}
            onFilterChange={setFilterType}
            allProducts={products}
            groups={groups}
          />
        </Main>
        <StoreInfoSidebar
          storeName={storeData.storeName}
          slug={storeData.slug || storeData._id}
          businessEmail={storeData.businessEmail || storeData.storeEmail}
          website={storeData.website}
          vatNumber={storeData.vatNumber}
          taxCode={storeData.taxCode}
          address={storeData.storeAddress || (storeData.storeAddress && [storeData.storeAddress]) || undefined}
          documents={storeData.documents || []}
        />
      </Layout>
    </Page>
  );
}
